import { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash-es';
/**
 * @export
 * A custom React hook that detects if a value has changed using deep comparison.
 * Returns true when the value changes from the previous render, false otherwise.
 *
 * @param value - The value to monitor for changes, can be of any type.
 * @returns A boolean indicating whether the value has changed since the last render.
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const [count, setCount] = useState(0);
 *   const hasCountChanged = useHasChanged(count);
 *
 *   return (
 *     <div>
 *       <p>Count: {count}</p>
 *       <p>Has changed: {hasCountChanged.toString()}</p>
 *       <button onClick={() => setCount(count + 1)}>Increment</button>
 *     </div>
 *   );
 * };
 * ```
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const [user, setUser] = useState({ name: 'John', age: 30 });
 *   const hasUserChanged = useHasChanged(user);
 *
 *   return (
 *     <div>
 *       <p>User: {JSON.stringify(user)}</p>
 *       <p>Has changed: {hasUserChanged.toString()}</p>
 *       <button onClick={() => setUser({ ...user, age: user.age + 1 })}>
 *         Update Age
 *       </button>
 *     </div>
 *   );
 * };
 * ```
 */
function useHasChanged(value) {
    const previousValueRef = useRef(undefined);
    const [hasChanged, setHasChanged] = useState(false);
    useEffect(() => {
        // Use deep comparison (lodash's isEqual) to detect changes
        if (!isEqual(previousValueRef.current, value)) {
            setHasChanged(true); // Set state when the value changes
        }
        else {
            setHasChanged(false);
        }
        // Update the ref to store the current value
        previousValueRef.current = value;
    }, [value]);
    return hasChanged;
}
export default useHasChanged;
